import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { showLeadershipCardOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { TotalRevenueChart } from '../../CompanyProfiles/Performance/components/Charts/TotalRevenueChart';
import { selectedCompanyIdProfileAtom } from '../../CompanyProfiles/state/UIStateJ';
import { FinancingHistoryTable } from '../../CompanyProfiles/Summary/components/FinancingHistory/FinancingHistoryTable';
import { NewsSection } from '../../CompanyProfiles/Summary/components/NewsSection';
import { CompanyDetailsPanel } from './CompanyDetailsPanel';
import { DigestPanel } from './DigestPanel';
import { EquityHoldingsPanel } from './EquityHoldings/EquityHoldingsPanel';
import { InvestmentMetricsPanel } from './InvestmentMetricsPanel';
import { LeadershipPanel } from './LeadershipPanel';
import { RecentActivityPanel } from './RecentActivity/RecentActivityPanel';
import { SummarySection } from './SummarySection';
import {
  CompanyProfilesContainerBreakpoint,
  DigestRailWidth,
  summaryCardCss,
  summaryPageBackground,
} from './summary-layout.styles';

const MainWrapper = styled('div')`
  background: ${summaryPageBackground};
  container-name: company-profiles-container;
  container-type: inline-size;
  display: grid;
  height: 100%;
  overflow: auto;
  width: 100%;
`;

/**
 * Two columns per design: a fixed 340px Digest rail and a single stack of sections beside it.
 * Both columns are top-aligned and content-height — the Digest is much shorter than the stack.
 *
 * Below the container breakpoint the rail cannot hold a 340px card next to anything, so the two
 * columns become one and the Digest sits above the stack.
 */
const SummaryLayout = styled('div')`
  align-content: start;
  align-items: start;
  display: grid;
  gap: 0.5rem;
  padding: 1rem;
  width: 100%;

  @container company-profiles-container (width < ${CompanyProfilesContainerBreakpoint}) {
    grid-template-columns: minmax(0, 1fr);
  }

  @container company-profiles-container (width >= ${CompanyProfilesContainerBreakpoint}) {
    grid-template-columns: ${DigestRailWidth} minmax(0, 1fr);
  }
`;

/** The right-hand column: every section stacked, 0.5rem apart, matching the design's card gap. */
const SectionStack = styled('div')`
  display: grid;
  gap: 0.5rem;
  grid-auto-rows: min-content;
  min-width: 0;
`;

export function CompanySummaryPortfolioV2() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const { useShowOwnershipTimeline } = useFlags<MaggieFeatureFlags>();
  const showLeadership = useAtomValue(showLeadershipCardOrgState);

  return (
    <MainWrapper>
      <SummaryLayout>
        <DigestPanel companyId={companyId} />
        {/* Section order: details, revenue, metrics, financing history, ownership, leadership,
            activity, news. Two cards are conditional: ownership on its `useShowOwnershipTimeline`
            flag, leadership on the org Data Display preference. */}
        <SectionStack>
          <div css={summaryCardCss}>
            <CompanyDetailsPanel companyId={companyId} />
          </div>
          <div css={summaryCardCss}>
            <TotalRevenueChart />
          </div>
          <div css={summaryCardCss}>
            <InvestmentMetricsPanel companyId={companyId} />
          </div>
          <div css={summaryCardCss}>
            <SummarySection title={'Financing History'}>
              <FinancingHistoryTable />
            </SummarySection>
          </div>
          {useShowOwnershipTimeline && (
            <div css={summaryCardCss}>
              {/* FD-4344 replaces the chart-and-holdings card with the two-view table. The design has
                  no ownership chart — the POC records it as parked pending real graphs — so the chart
                  and its view-mode toggle live on only in the flag-off V1 layout. */}
              <EquityHoldingsPanel companyId={companyId} />
            </div>
          )}
          {showLeadership && (
            <div css={summaryCardCss}>
              <LeadershipPanel companyId={companyId} />
            </div>
          )}
          <div css={summaryCardCss}>
            <RecentActivityPanel />
          </div>
          <div css={summaryCardCss}>
            <SummarySection title={'Recent News & Press'}>
              <NewsSection />
            </SummarySection>
          </div>
        </SectionStack>
      </SummaryLayout>
    </MainWrapper>
  );
}
